<template>
  <div v-if="!isLoading && product">
    <!-- PhotoSwipe: скрытая разметка для слайдов -->
    <div :id="pswpGalleryId" :class="$style.pswpGallery" aria-hidden="true">
      <a
        v-for="(url, idx) in product.modelImages"
        :key="`pswp-${idx}`"
        :href="url"
        target="_blank"
        rel="noreferrer"
        :data-pswp-width="pswpSizes?.[idx]?.w || 1600"
        :data-pswp-height="pswpSizes?.[idx]?.h || 1600"
        :data-cropped="true"
      >
        {{ product.modelTitle }} {{ idx + 1 }}
      </a>
    </div>

    <!-- Галерея + лайк поверх -->
    <div :class="$style.galleryWrap">
      <ProductGallerySimple
        :images="product.modelImages"
        :alt-base="product.modelTitle"
        @open="openPhotoSwipe"
      />
      <button :class="$style.mainFavBtn" type="button" @click="toggleFavorite">
        <img
          v-if="!product.isFavorite"
          src="@/assets/img/market/like.svg"
          alt=""
        />
        <img v-else src="@/assets/img/market/fill-like.svg" alt="" />
      </button>
    </div>

    <div
      :class="[
        $style.wrapper,
        {
          [$style.wrapperWithBid]: hasBidControls,
        },
      ]"
    >
      <!-- текстовый блок: название, цены, инфо -->
      <div :class="$style.infoBlock">
        <h1 :class="$style.title">
          {{ product.modelTitle }}
        </h1>

        <div :class="$style.priceRow">
          <div :class="$style.priceContainer">
            <span :class="$style.priceCurrent">
              {{ formatPrice(product.price) }} ₽
            </span>
            <div
              v-if="product.avgPriceDeltaPercent !== null"
              :class="[
                $style.priceDelta,
                product.avgPriceDeltaPercent >= 0
                  ? $style.priceDeltaGreenBg
                  : $style.priceDeltaRedBg,
              ]"
            >
              <img
                v-if="product.avgPriceDeltaPercent >= 0"
                src="@/assets/img/market/green-percent.svg"
                alt=""
              />
              <img v-else src="@/assets/img/market/red-percent.svg" alt="" />
              <span
                :class="[
                  $style.priceDeltaText,
                  product.avgPriceDeltaPercent >= 0
                    ? $style.priceDeltaGreen
                    : $style.priceDeltaRed,
                ]"
              >
                {{ Math.abs(product.avgPriceDeltaPercent) }}%
              </span>
            </div>
          </div>
          <span v-if="product.oldPrice" :class="$style.priceOld">
            {{ formatPrice(product.oldPrice) }} ₽
          </span>
        </div>

        <div :class="$style.meta">
          <div v-if="product.brand">
            <span :class="$style.metaLabel">Бренд:</span>
            <span :class="$style.metaValue">{{ product.brand }}</span>
          </div>
          <div v-if="categoryLabel">
            <span :class="$style.metaLabel">Категория:</span>
            <span :class="$style.metaValue">{{ categoryLabel }}</span>
          </div>
          <div v-if="product.colour">
            <span :class="$style.metaLabel">Цвет:</span>
            <span :class="$style.metaValue">
              {{ translateColor(product.colour) }}
            </span>
          </div>
        </div>

        <div :class="$style.meta">
          <div>
            <span :class="$style.metaLabel">Артикул (SKU)</span>
            <span :class="$style.metaValue">{{ product.modelArticle }}</span>
          </div>

          <div v-if="product.sellerRatingCount > 0">
            <span :class="$style.metaLabel">Рейтинг продавца</span>
            <span :class="$style.metaValue">
              {{ product.sellerRating }}
              <span :class="$style.metaStar">★</span>
            </span>
          </div>

          <div>
            <span :class="$style.metaLabel">Когда можно забрать?</span>
            <span :class="$style.metaValue">
              {{ product.pickupLabel }}
            </span>
          </div>
        </div>

        <div v-if="hasSizes" :class="$style.sizesSection">
          <div :class="$style.sizeTabs">
            <button
              v-for="type in sizeTabs"
              :key="type"
              type="button"
              :class="[
                $style.sizeTab,
                type === activeSizeType && $style.sizeTabActive,
              ]"
              @click="setActiveSize(type)"
            >
              {{ getSizeTypeLabel(type, true) }}
            </button>
          </div>

          <div :class="$style.sizeScrollWrap">
            <DragScroll
              ref="sizeDrag"
              :className="$style.sizeScrollArea"
              :interactive="true"
            >
              <button
                v-for="size in activeSizes"
                :key="`${activeSizeType}-${size.id}`"
                type="button"
                :disabled="!size.isAvailable"
                :class="[
                  $style.sizeCell,
                  !size.isAvailable && $style.sizeCellDisabled,
                  selectedSizeKey === `${activeSizeType}-${size.id}` &&
                    $style.sizeCellSelected,
                ]"
                @click="selectSize(activeSizeType, size)"
              >
                {{ formatSizeLabel(size.value) }}
              </button>
            </DragScroll>
            <div
              v-if="sizeScrollable && !sizeAtEnd"
              :class="$style.sizeScrollFade"
              aria-hidden="true"
            ></div>
            <button
              v-if="sizeScrollable && !sizeAtEnd"
              :class="$style.sizeScrollHint"
              type="button"
              aria-label="Прокрутить размеры"
              @click.stop="scrollSizesRight"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <!-- карточка продавца -->
      <button :class="$style.sellerCard" type="button" @click="openSeller">
        <div :class="$style.sellerWrap">
          <div v-if="product.sellerPhoto" :class="$style.sellerAvatar">
            <img :src="product.sellerPhoto" alt="">
          </div>
          <div :class="$style.sellerText">
            <div :class="$style.sellerName">
              {{ product.sellerName }}
            </div>
            <div v-if="product.sellerRatingCount > 0" :class="$style.sellerRating">
              Рейтинг:
              <span :class="$style.sellerRatingValue">
                {{ product.sellerRating }}
              </span>
              <span :class="$style.sellerRatingStar">★</span>
            </div>
          </div>
        </div>
        <span :class="$style.sellerChevron">
          <img src="@/assets/img/market/chev.svg" alt="" />
        </span>
      </button>

      <!-- Элемент управления количеством для reseller -->
      <div
        v-if="$store.state.user.type === 'reseller' && !isOwnProduct && isInBid"
        :class="$style.bidControls"
      >
        <button :class="$style.bidInfoBtn" type="button" @click="openBid">
          <div :class="$style.bidInfoText">
            <div>В заявке</div>
            <div :class="$style.bidInfoSizes">
              {{ sizesTotal }} {{ getSizesLabel(sizesTotal) }}
            </div>
          </div>
        </button>
        <div :class="$style.quantityControls">
          <button
            :class="$style.quantityBtn"
            type="button"
            @click="decreaseQuantity"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12H19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <span :class="$style.quantityValue">{{ bidQuantity }}</span>
          <button
            :class="$style.quantityBtn"
            type="button"
            @click="increaseQuantity"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-else :class="$style.wrapper">
    <div :class="$style.gallerySkeleton">
      <div :class="['skeleton', $style.gallerySkeletonBox]"></div>
    </div>
    <div :class="$style.infoSkeleton">
      <div :class="['skeleton', $style.titleSkeleton]"></div>
      <div :class="['skeleton', $style.priceSkeleton]"></div>
      <div
        v-for="n in 3"
        :key="`meta-skeleton-${n}`"
        :class="['skeleton', $style.metaSkeletonLine]"
      ></div>
      <div :class="['skeleton', $style.sizeSkeleton]"></div>
      <div :class="['skeleton', $style.sellerSkeleton]"></div>
    </div>
  </div>
</template>

<script>
import ProductGallerySimple from "@/components/ProductGallerySimple.vue"
import PhotoSwipeLightbox from "photoswipe/lightbox"
import "photoswipe/style.css"
import DragScroll from "@/components/DragScroll.vue"
import { translateColor } from "@/utils/colorTranslate.js"
import {
  SIZE_CHART,
  convertSize as convertSizeUtil,
} from "@/utils/sizeChart.js"

export default {
  name: "Product",
  components: { ProductGallerySimple, DragScroll },

  data() {
    return {
      product: null,
      activeSizeType: null,
      selectedSizeKey: null,
      isLoading: true,
      mainButtonInstance: null,
      mbHandler: null,
      sizeConversionTable: SIZE_CHART, // Используем общую размерную сетку
      pswpGalleryId: "pswp-product-gallery",
      pswpLightbox: null,
      pswpSizes: [],
      pswpResizeHandler: null,
      tgMainButtonWasVisible: null,
      sizeScrollable: false,
      sizeAtEnd: false,
    }
  },

  computed: {
    isOwnProduct() {
      const currentUser = this.$store.state.user

      return (
        currentUser.type === "seller" &&
        String(currentUser.id) === String(this.product.sellerId)
      )
    },
    user() {
      return this.$store.state.user || {}
    },
    isBidDisabled() {
      return this.user.moderation_status === "pending"
    },
    productId() {
      return this.$route.params.id
    },

    sizeTabs() {
      if (!this.product?.sizesByType) return []
      return this.getOrderedSizeTypes(Object.keys(this.product.sizesByType))
    },

    activeSizes() {
      if (!this.activeSizeType || !this.product?.sizesByType) return []
      return this.product.sizesByType[this.activeSizeType] || []
    },

    hasSizes() {
      return this.activeSizes.length > 0 || this.sizeTabs.length > 0
    },

    sizesTotal() {
      if (!this.product?.modelId) return 0

      // Находим все продукты в заявке с тем же modelId
      const bidItemsWithSameModel = this.$store.state.bid.filter(
        (item) =>
          item.modelId && String(item.modelId) === String(this.product.modelId)
      )

      if (bidItemsWithSameModel.length === 0) return 0

      // Считаем количество уникальных размеров (size + sizeType)
      const uniqueSizes = new Set()
      bidItemsWithSameModel.forEach((item) => {
        if (item.size && item.sizeType) {
          uniqueSizes.add(`${item.size}-${item.sizeType}`)
        }
      })

      return uniqueSizes.size
    },
    isInBid() {
      if (!this.product) return false
      return this.$store.state.bid.some((item) => item.id === this.product.id)
    },
    bidQuantity() {
      if (!this.product) return 0
      const bidItem = this.$store.state.bid.find(
        (item) => item.id === this.product.id
      )
      return bidItem?.quantity || 0
    },
    hasBidControls() {
      return (
        this.$store.state.user.type === "reseller" &&
        !this.isOwnProduct &&
        this.isInBid
      )
    },
    categoryLabel() {
      const map = {
        MALE: "Мужские",
        FEMALE: "Женские",
        UNISEX: "Унисекс",
        TEENAGER: "Детские",
      }
      const cat = this.product?.category
      if (!cat) return null
      return map[cat] || cat
    },
  },

  watch: {
    sizeTabs(newTabs) {
      if (!newTabs.length) {
        this.activeSizeType = null
        return
      }

      if (!newTabs.includes(this.activeSizeType)) {
        this.activeSizeType = newTabs[0]
      }
    },
    // Смена набора размеров/сетки — пересчитать индикатор прокрутки
    activeSizes() {
      this.$nextTick(this.updateSizeScrollState)
    },
    activeSizeType() {
      this.$nextTick(this.updateSizeScrollState)
    },
    isInBid() {
      this.updateMainButton()
    },
    "$store.state.bid": {
      handler() {
        this.updateMainButton()
      },
      deep: true,
    },
    "$store.state.user": {
      handler() {
        this.updateMainButton()
      },
      deep: true,
    },
  },

  async mounted() {
    await this.fetchProduct()
    this.setupMainButton()
    this.updatePhotoSwipeCssVars()
    this.refreshPhotoSwipe()

    this.pswpResizeHandler = () => {
      this.updatePhotoSwipeCssVars()
    }
    window.addEventListener("resize", this.pswpResizeHandler)
    window.addEventListener("orientationchange", this.pswpResizeHandler)

    // Индикатор прокрутки ряда размеров
    this.$nextTick(() => {
      const el = this.getSizeScrollEl()
      if (el) {
        el.addEventListener("scroll", this.onSizeScroll, { passive: true })
      }
      this.updateSizeScrollState()
    })
  },

  beforeUnmount() {
    this.destroyMainButton()
    this.destroyPhotoSwipe()
    const sizeEl = this.getSizeScrollEl()
    if (sizeEl) {
      sizeEl.removeEventListener("scroll", this.onSizeScroll)
    }
    if (this.pswpResizeHandler) {
      window.removeEventListener("resize", this.pswpResizeHandler)
      window.removeEventListener("orientationchange", this.pswpResizeHandler)
      this.pswpResizeHandler = null
    }
  },

  methods: {
    translateColor(color) {
      return translateColor(color)
    },

    // ── Индикатор «скроллится вправо» у ряда размеров ──
    getSizeScrollEl() {
      return this.$refs.sizeDrag?.$refs?.container || null
    },
    updateSizeScrollState() {
      const el = this.getSizeScrollEl()
      if (!el) {
        this.sizeScrollable = false
        this.sizeAtEnd = false
        return
      }
      this.sizeScrollable = el.scrollWidth - el.clientWidth > 8
      this.sizeAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8
    },
    scrollSizesRight() {
      const el = this.getSizeScrollEl()
      if (!el) return
      el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" })
    },
    onSizeScroll() {
      this.updateSizeScrollState()
    },

    formatPrice(value) {
      if (value === null || value === undefined) return ""
      return Number(value).toLocaleString("ru-RU")
    },
    formatPickupTime(seconds) {
      const sec = Number(seconds)
      if (!Number.isFinite(sec) || sec <= 0) return null

      const totalHours = Math.max(1, Math.ceil(sec / 3600))
      const days = Math.floor(totalHours / 24)

      if (days >= 1) {
        const roundedDays = Math.max(1, Math.ceil(totalHours / 24))
        return `${roundedDays} д`
      }

      return `${totalHours} ч`
    },
    // «Когда можно забрать?»:
    //  - Excel-импорт: от времени суток (локальное время покупателя) и
    //    in_stock — «В наличии»: до 12 → сегодня, после → завтра;
    //    «Под заказ»: до 12 → завтра, после → послезавтра.
    //  - Ручное добавление: как раньше — из time_to_pickup_sec селлера
    //    («Спустя N д/N ч», иначе «Скоро»).
    computePickupLabel({ isImported, inStock, pickupDate }) {
      if (!isImported) {
        return pickupDate ? `Спустя ${pickupDate}` : "Скоро"
      }
      const hours = new Date().getHours()
      if (inStock) {
        return hours < 12 ? "Сегодня" : "Завтра"
      }
      return hours < 12 ? "Завтра" : "Послезавтра"
    },
    refreshPhotoSwipe() {
      this.destroyPhotoSwipe()
      this.$nextTick(() => {
        if (!this.product?.modelImages?.length) return
        this.initPhotoSwipe()
      })
    },
    updatePhotoSwipeCssVars() {
      // Telegram WebApp fullscreen часто рисует собственную верхнюю панель,
      // из-за чего стандартный top-bar PhotoSwipe залезает "слишком высоко".
      // Сдвигаем контролы вниз через CSS-переменные.
      const tg = window.Telegram?.WebApp
      const safeTop = Number(tg?.safeAreaInset?.top || 0)
      const safeBottom = Number(tg?.safeAreaInset?.bottom || 0)
      const isFullscreen = Boolean(tg?.isFullscreen)

      // Эмпирический отступ под верхнюю панель Telegram в fullscreen.
      // safeAreaInset.top там часто 0, поэтому добавляем baseline.
      const tgTopOverlay = isFullscreen ? 52 : 0

      const topOffsetPx = safeTop + tgTopOverlay + 10
      const bottomOffsetPx = safeBottom + 10

      document.documentElement.style.setProperty(
        "--pswp-tg-top-offset",
        `${topOffsetPx}px`
      )
      document.documentElement.style.setProperty(
        "--pswp-tg-bottom-offset",
        `${bottomOffsetPx}px`
      )
    },
    initPhotoSwipe() {
      if (this.pswpLightbox) return

      this.pswpLightbox = new PhotoSwipeLightbox({
        gallery: `#${this.pswpGalleryId}`,
        children: "a",
        showHideAnimationType: "zoom",
        bgOpacity: 0.9,
        wheelToZoom: true,
        pswpModule: () => import("photoswipe"),
      })

      // Telegram MainButton: hide when lightbox opens, restore after it closes
      // PhotoSwipe v5 core events: https://photoswipe.com/events
      this.pswpLightbox.on("beforeOpen", () => {
        const tg = window.Telegram?.WebApp
        const mb = tg?.MainButton
        this.tgMainButtonWasVisible = mb ? Boolean(mb.isVisible) : null
        if (mb?.hide) mb.hide()
      })

      this.pswpLightbox.on("closingAnimationEnd", () => {
        // Restore to proper state for this page
        this.updateMainButton()
      })

      this.pswpLightbox.init()
    },
    destroyPhotoSwipe() {
      if (this.pswpLightbox) {
        this.pswpLightbox.destroy()
        this.pswpLightbox = null
      }
      // If gallery is destroyed while open, ensure MainButton returns to correct state
      this.updateMainButton()
    },
    openPhotoSwipe(idx = 0) {
      this.updatePhotoSwipeCssVars()
      this.initPhotoSwipe()
      this.pswpLightbox?.loadAndOpen(idx)
    },
    preloadPhotoSwipeSizes(urls = []) {
      if (!urls?.length) return
      this.pswpSizes = urls.map(() => null)

      urls.forEach((url, idx) => {
        const img = new Image()
        img.onload = () => {
          this.pswpSizes[idx] = {
            w: img.naturalWidth || 1600,
            h: img.naturalHeight || 1600,
          }
        }
        img.src = url
      })
    },

    // Получение инфы о продукте с сервера
    async fetchProduct() {
      this.isLoading = true
      try {
        const response = await this.$store.dispatch("getProduct", {
          id: this.productId,
        })

        const data = response?.data || response

        const apiModel = data.model || {}
        const apiProduct = data.product || {}
        const apiSeller = data.seller || {}
        const apiSizes = data.sizes?.items || []

        // Конвертируем размеры из EU во все доступные типы
        const groupedSizes = {}
        const sizeTypes = ["EU", "RU", "US", "UK", "CM"]

        // Инициализируем массивы для всех типов
        sizeTypes.forEach((type) => {
          groupedSizes[type] = []
        })

        // Обрабатываем размеры с сервера (они приходят в EU)
        apiSizes.forEach((sizeItem) => {
          const euSize = parseFloat(sizeItem.size) || 0
          const isAvailable = sizeItem.is_available

          // Конвертируем EU размер во все остальные типы
          sizeTypes.forEach((targetType) => {
            const convertedSize = this.convertSize(euSize, "EU", targetType)
            if (convertedSize !== null) {
              groupedSizes[targetType].push({
                id: sizeItem.id,
                value: convertedSize,
                originalEuSize: euSize, // Сохраняем оригинальный EU размер
                isAvailable: isAvailable,
              })
            }
          })
        })

        // Сортируем размеры в каждом типе
        Object.keys(groupedSizes).forEach((type) => {
          groupedSizes[type].sort(
            (a, b) =>
              this.parseSizeValue(a.value) - this.parseSizeValue(b.value)
          )
        })

        // Обрабатываем кастомные изображения
        const customImages = apiProduct.custom_images || []
        const customImageUrls = customImages
          .map((item) => {
            // Извлекаем URL из объекта или строки
            return typeof item === "string" ? item : item?.url
          })
          .filter((url) => url) // Фильтруем пустые значения

        // Объединяем кастомные изображения (первыми) с основными изображениями модели
        const allImages = [...customImageUrls, ...(apiModel.images || [])]

        const pickupDate = this.formatPickupTime(apiSeller?.time_to_pickup_sec)
        const isImported = apiProduct.is_imported === true
        const inStock = apiProduct.in_stock !== false
        const pickupLabel = this.computePickupLabel({
          isImported,
          inStock,
          pickupDate,
        })

        this.product = {
          id: apiProduct.id,
          modelId: apiProduct.model_id,
          modelTitle: apiModel.title,
          modelImages: allImages,
          modelArticle: apiModel.article || "Не указан",
          brand: apiModel.brand || null,
          category: apiModel.category || null,
          colour: apiModel.colour || null,
          price: apiProduct.price ? Number(apiProduct.price) : 0,
          size: apiProduct.size,
          sizeType: apiProduct.size_type,
          isTrade: apiProduct.is_trade || false,
          sellerId: apiSeller.id,
          sellerName: apiSeller.login || apiSeller.username || "Продавец",
          sellerRating: apiSeller.rating ? Number(apiSeller.rating) : 0,
          sellerRatingCount: apiSeller.rating_count ?? 0,
          sellerPhoto: apiSeller.photo_url || null,
          pickupDate,
          pickupLabel,
          isImported,
          inStock,
          isFavorite: !!data.is_favorite,
          sizesByType: groupedSizes,
          sizesTotal: apiSizes.length,
          // Количество доступных продуктов
          count:
            apiProduct.count !== undefined && apiProduct.count !== null
              ? Number(apiProduct.count)
              : null,
          // Данные модели с сервера
          total_count:
            data.total_count !== undefined ? Number(data.total_count) : null,
          min_price:
            data.min_price !== undefined ? Number(data.min_price) : null,
          avgPriceDeltaPercent:
            apiProduct.avg_price_delta_percent !== undefined &&
            apiProduct.avg_price_delta_percent !== null
              ? Number(apiProduct.avg_price_delta_percent)
              : null,
        }

        // По умолчанию показываем EU размеры
        this.activeSizeType = "EU"

        // Select the specific product ID if available
        if (this.activeSizeType && apiProduct.id) {
          this.selectedSizeKey = `${this.activeSizeType}-${apiProduct.id}`
        } else {
          this.selectedSizeKey = null
        }

        // Обновляем кнопку после получения данных, так как isOwnProduct зависит от this.product
        this.updateMainButton()

        // PhotoSwipe: проставляем размеры картинок (для корректного зума/анимаций)
        this.preloadPhotoSwipeSizes(this.product.modelImages || [])
        this.refreshPhotoSwipe()
      } finally {
        this.isLoading = false
      }
    },

    setupMainButton() {
      const tg = window.Telegram?.WebApp
      const mb = tg?.MainButton
      if (!mb) return

      this.mainButtonInstance = mb
      this.updateMainButton()
    },

    updateMainButton() {
      const mb = this.mainButtonInstance
      if (!mb) return

      if (this.isOwnProduct) {
        mb.hide()
      } else if (this.$store.state.user.type === "reseller" && !this.isInBid) {
        if (this.mbHandler) {
          mb.offClick(this.mbHandler)
        }
        this.mbHandler = this.handleAddToBid

        mb.setText("Добавить в заявку")
        if (this.isBidDisabled) {
          mb.color = "#E8E8EA" // Gray color when disabled
          mb.textColor = "#B9B9BB"
          if (mb.disable) {
            mb.disable()
          }
        } else {
          mb.color = "#f07818" // Orange color
          mb.textColor = "#ffffff"
          if (mb.enable) {
            mb.enable()
          }
        }
        mb.onClick(this.mbHandler)
        mb.show()
      } else {
        mb.hide()
      }
    },

    destroyMainButton() {
      const mb = this.mainButtonInstance
      if (mb) {
        if (this.mbHandler) {
          mb.offClick(this.mbHandler)
        }
        mb.hide()
      }
      this.mainButtonInstance = null
      this.mbHandler = null
    },

    handleAddToBid() {
      if (!this.product) return
      if (this.isBidDisabled) {
        return false
      }

      // Получаем выбранный размер и конвертируем его в EU
      let euSize = this.product.size
      let euSizeType = "EU"

      if (this.selectedSizeKey) {
        const [selectedType, productId] = this.selectedSizeKey.split("-")
        const selectedSizeItem = this.activeSizes.find(
          (s) => String(s.id) === productId
        )

        if (selectedSizeItem && selectedSizeItem.originalEuSize !== undefined) {
          // Используем сохраненный оригинальный EU размер
          euSize = selectedSizeItem.originalEuSize
          euSizeType = "EU"
        } else if (selectedSizeItem) {
          // Конвертируем выбранный размер обратно в EU
          const selectedSizeValue = parseFloat(selectedSizeItem.value) || 0
          euSize = this.convertSize(selectedSizeValue, selectedType, "EU")
          euSizeType = "EU"
        }
      }

      const bidItem = {
        // Информация о продукте
        id: this.product.id,
        modelId: this.product.modelId,
        price: this.product.price,
        size: String(euSize), // Всегда отправляем размер в EU формате
        sizeType: euSizeType, // Всегда отправляем тип EU
        isTrade: this.product.isTrade,
        sellerId: this.product.sellerId,
        sellerPhoto: this.product.sellerPhoto || null,
        sellerName: this.product.sellerName,
        sellerRating: this.product.sellerRating,
        // Информация о модели
        modelTitle: this.product.modelTitle,
        modelImages: this.product.modelImages,
        modelArticle: this.product.modelArticle,
        preview_image: this.product.modelImages?.[0] || null,
        // Данные модели с сервера
        total_count: this.product.total_count,
        min_price: this.product.min_price,
        // Максимальное доступное количество продукта
        maxCount:
          this.product.count !== undefined ? Number(this.product.count) : null,
        // Количество
        quantity: 1,
        // Пользовательская цена (если isTrade)
        customPrice: null,
      }

      try {
        this.$store.commit("addToBid", bidItem)
        this.updateMainButton()
      } catch (error) {
        window.alert(error.message || "Не удалось добавить товар в заявку")
      }
    },
    increaseQuantity() {
      if (!this.product) return
      const currentQuantity = this.bidQuantity
      this.$store.commit("updateBidQuantity", {
        productId: this.product.id,
        quantity: currentQuantity + 1,
      })
    },
    decreaseQuantity() {
      if (!this.product) return
      const currentQuantity = this.bidQuantity
      if (currentQuantity > 1) {
        this.$store.commit("updateBidQuantity", {
          productId: this.product.id,
          quantity: currentQuantity - 1,
        })
      } else {
        // Если количество становится 0, удаляем из заявки
        this.$store.commit("removeFromBid", this.product.id)
        this.updateMainButton()
      }
    },
    openBid() {
      this.$router.push({ name: "Bid" })
    },
    getSizesLabel(count) {
      const lastDigit = count % 10
      const lastTwoDigits = count % 100

      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return "размеров"
      }
      if (lastDigit === 1) {
        return "размер"
      }
      if (lastDigit >= 2 && lastDigit <= 4) {
        return "размера"
      }
      return "размеров"
    },

    // лайк / избранное
    async toggleFavorite() {
      if (!this.product) return

      if (this.product.isFavorite) {
        await this.$store.dispatch("deleteFavorite", {
          product_id: this.product.id,
        })
        this.product.isFavorite = false
      } else {
        await this.$store.dispatch("addFavorite", {
          product_id: this.product.id,
        })
        this.product.isFavorite = true
      }
    },

    setActiveSize(type) {
      if (type === this.activeSizeType) return
      this.activeSizeType = type

      // При переключении типа размера находим соответствующий размер для текущего продукта
      // Все размеры с одинаковым ID - это один и тот же продукт в разных размерных системах
      const sizesForType = this.product.sizesByType?.[type] || []
      const currentProductInList = sizesForType.find(
        (s) => String(s.id) === String(this.product.id)
      )

      if (currentProductInList) {
        this.selectedSizeKey = `${type}-${this.product.id}`
      } else {
        // Если текущий продукт не найден в новом типе, сбрасываем выбор
        this.selectedSizeKey = null
      }
    },

    selectSize(type, size) {
      if (!size?.isAvailable) return
      const newSizeKey = `${type}-${size.id}`
      if (this.selectedSizeKey === newSizeKey) return

      this.selectedSizeKey = newSizeKey

      // Navigate to the new product ID to load its details
      // Все размеры с одинаковым ID - это один и тот же продукт, просто в разных размерных системах
      if (size.id && String(size.id) !== String(this.product.id)) {
        this.$router.replace({
          name: "Product",
          params: { id: size.id },
        })
        // Trigger fetch manually since the route param change might not re-trigger mounted in some setups if not watching $route
        setTimeout(() => {
          this.fetchProduct()
        }, 0)
      }
    },
    openSeller() {
      if (!this.product?.sellerId) return
      this.$router.push({
        name: "Seller",
        params: { id: this.product.sellerId },
      })
    },

    getOrderedSizeTypes(types) {
      if (!types?.length) return []
      const preferredOrder = ["EU", "RU", "US", "UK", "CM"]
      const normalized = types.map((type) => type.toUpperCase())
      const ordered = preferredOrder.filter((type) => normalized.includes(type))
      const remaining = normalized
        .filter((type) => !preferredOrder.includes(type))
        .sort()
      return [...ordered, ...remaining]
    },

    getSizeTypeLabel(type, short = false) {
      const t = String(type || "").toUpperCase()
      return short ? t : t
    },

    // Конвертация размеров между типами
    convertSize(size, fromType, toType) {
      if (!size || fromType === toType) return size

      const numSize = parseFloat(String(size).replace(/[^\d.]/g, ""))
      if (isNaN(numSize)) return null

      // Конвертируем сначала в EU (базовый тип)
      let euSize = numSize
      if (fromType !== "EU") {
        euSize = this.toEU(size, fromType)
        if (euSize === null) return null
      }

      // Затем конвертируем из EU в целевой тип
      if (toType === "EU") {
        return euSize
      } else {
        return this.fromEU(euSize, toType)
      }
    },

    // Конвертация в EU (базовый тип)
    toEU(size, fromType) {
      return convertSizeUtil(size, fromType, "EU")
    },

    // Конвертация из EU в другие типы
    fromEU(euSize, toType) {
      return convertSizeUtil(euSize, "EU", toType)
    },

    parseSizeValue(value) {
      const normalized = Number(String(value ?? "").replace(",", "."))
      return Number.isNaN(normalized) ? Number.MAX_VALUE : normalized
    },

    formatSizeLabel(value) {
      if (value === null || value === undefined) return ""
      // Для строковых значений (US, UK с суффиксами) возвращаем как есть
      if (
        typeof value === "string" &&
        (value.includes("C") || value.includes("Y") || value.includes("(M)"))
      ) {
        return value
      }
      // Для числовых значений заменяем точку на запятую
      return String(value).replace(".", ",")
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 1rem;
}

.wrapperWithBid {
  padding: 1rem 1rem 6rem 1rem;
}

/* галерея + лайк */
.galleryWrap {
  position: relative;
  margin-bottom: 1rem;
}

/* кнопка лайка поверх галереи */
.mainFavBtn {
  position: absolute;
  right: 1rem;
  top: 1rem;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  padding: 0;
  z-index: 10;
  width: 2.5rem;
  height: 2.5rem;

  img {
    width: 100%;
    height: 100%;
  }
}

/* блок текста */
.infoBlock {
  margin-bottom: 1.5rem;
}

.title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.priceRow {
  display: flex;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 1rem;
}

.priceContainer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.priceCurrent {
  font-size: 1.375rem;
  font-weight: 700;
}

.priceDelta {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 1rem;
  padding: 0 0.5rem 0 0;
}

.priceDelta img {
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
}

.priceDeltaText {
  font-size: 0.875rem;
  font-weight: 600;
}

.priceDeltaGreenBg {
  background: #58ce661a;
}

.priceDeltaRedBg {
  background-color: #ff3a302e;
}

.priceDeltaGreen {
  color: #58ce66;
}

.priceDeltaRed {
  color: #e53935;
}

.priceOld {
  font-size: 1rem;
  text-decoration: line-through;
  opacity: 0.5;
}

.meta {
  font-size: 0.875rem;
}

.meta > div {
  margin-bottom: 0.25rem;
}

.metaLabel {
  margin-right: 0.25rem;
  font-weight: 300;
}

.metaValue {
  font-weight: 500;
}

.metaStar {
  color: #f7bf18;
  margin-left: 0.15rem;
}

.sizesSection {
  margin-top: 1.5rem;
}

.sizeTabs {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #9b9b9b;
  position: relative;
}

.sizeTab {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  padding: 0.5rem 0;
  text-align: center;
  color: #0a0a0a;
  cursor: pointer;
  position: relative;
  font-weight: 300;
}

.sizeTabActive {
  color: #0a0a0a;
  font-weight: 600;
}

.sizeTabActive::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 100%;
  height: 2px;
  background: #0a0a0a;
  border-radius: 0.25rem;
}

.sizeScrollWrap {
  position: relative;
}

.sizeScrollArea {
  display: flex;
  gap: 0.25rem;
  overflow-x: auto;
  padding: 0.5rem 0;
  scrollbar-width: none;
}

.sizeScrollArea::-webkit-scrollbar {
  display: none;
}

/* Затухание и шеврон справа — подсказка, что ряд прокручивается */
.sizeScrollFade {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 2.5rem;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.9) 55%,
    #ffffff
  );
  pointer-events: none;
}

.sizeScrollHint {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1.5rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: #0a0a0a;
  font-size: 1.5rem;
  line-height: 1;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  z-index: 2;
}

.sizeCell {
  border: none;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 300;
  color: #0a0a0a;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: background 0.2s, color 0.2s;
}

.sizeCellSelected {
  background: #f5f7fa;
  font-weight: 600;
}

.sizeCellDisabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* no custom scrollbar when используем DragScroll */

/* карточка продавца */
.sellerCard {
  width: 100%;
  border-radius: 0.625rem;
  background: transparent;
  border: 1px solid #ebeaea;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sellerWrap {
  display: flex;
  align-items: center;
}

.sellerAvatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 100%;
  background: #000;
  margin-right: 0.625rem;
  img {
    width: 100%;
    height: 100%;
  }
}

.sellerText {
  justify-content: flex-start;
  text-align: left;
}

.sellerName {
  font-size: 1rem;
  font-weight: 500;
}

.sellerRating {
  font-size: 0.75rem;
  font-weight: 400;
}

.sellerRatingValue {
  font-weight: 600;
  margin-left: 0.25rem;
}

.sellerRatingStar {
  color: #f7bf18;
  margin-left: 0.125rem;
}

.gallerySkeleton {
  position: relative;
  margin-bottom: 1rem;
}

.gallerySkeletonBox {
  width: 100%;
  height: 14rem;
  border-radius: 0.75rem;
}

.infoSkeleton {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.titleSkeleton {
  height: 1.5rem;
  border-radius: 0.5rem;
}

.priceSkeleton {
  width: 50%;
  height: 1.25rem;
  border-radius: 0.5rem;
}

.metaSkeletonLine {
  height: 0.875rem;
  border-radius: 0.5rem;
}

.sizeSkeleton {
  height: 2.5rem;
  border-radius: 0.5rem;
}

.sellerSkeleton {
  height: 3.25rem;
  border-radius: 0.75rem;
}

/* Элемент управления количеством */
.bidControls {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 0.625rem;
  padding: 0.5rem 1rem 1.25rem 1rem;
  align-items: center;
  background: #f2f2f2;
  border-top: 1px solid #b2b2b2;
  z-index: 100;
}

.bidInfoBtn {
  flex: 1;
  background: #000;
  color: #fff;
  border: none;
  border-radius: 0.625rem;
  padding: 0.5rem 0;
  text-align: center;
  cursor: pointer;
}

.bidInfoText {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  line-height: 1.2;
  color: inherit;
}

.bidInfoText div {
  color: inherit;
}

.bidInfoSizes {
  font-size: 0.75rem;
  opacity: 0.7;
}

.quantityControls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border-radius: 0.625rem;
  padding: 0.625rem 1rem;
}

.quantityBtn {
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #ec6608;
  cursor: pointer;
  padding: 0;
  border-radius: 0.25rem;
  transition: background 0.2s;

  &:hover {
    background: #f5f7fa;
  }

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
}

.quantityValue {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
  min-width: 1.5rem;
  text-align: center;
}

.pswpGallery {
  display: none;
}

/* PhotoSwipe: only offset controls down (Telegram fullscreen + safe areas) */
:global(.pswp__top-bar) {
  top: 0;
  padding-top: 12vh;
  height: auto;
}
</style>
